import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, orderBy, where, Timestamp, doc, updateDoc, documentId } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from './firebase';
import './ManageReviews.css';

interface Review {
  id: string;
  bookingId: string;
  customerId: string;
  providerId: string;
  customerName: string;
  providerName: string;
  rating: number;
  comment?: string;
  createdAt: Timestamp;
  status: 'active' | 'inactive';
}

const ManageReviews: React.FC = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [filteredReviews, setFilteredReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 50;
  
  // Inline editing states
  const [editingReviewId, setEditingReviewId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Fetch reviews
  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      let reviewsQueryRef;
      let querySnapshot;

      // Server-side constraints for status/rating
      const constraints: any[] = [];
      if (statusFilter !== 'all') {
        constraints.push(where('status', '==', statusFilter));
      }
      if (ratingFilter !== 'all') {
        constraints.push(where('rating', '==', parseInt(ratingFilter)));
      }

      try {
        reviewsQueryRef = query(
          collection(db, 'reviews'),
          ...constraints,
          orderBy('createdAt', 'desc')
        );
        querySnapshot = await getDocs(reviewsQueryRef);
      } catch (indexError) {
        // Fallback nếu thiếu index
        reviewsQueryRef = query(
          collection(db, 'reviews'),
          ...constraints
        );
        querySnapshot = await getDocs(reviewsQueryRef);
      }
      const reviewsData: Review[] = [];

      // Get all unique user IDs (both customers and providers)
      const userIds = new Set<string>();
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.customerId) userIds.add(data.customerId);
        if (data.providerId) userIds.add(data.providerId);
      });

      // Batch fetch user names by documentId in chunks of 10
      const userNames = new Map<string, string>();
      const idList = Array.from(userIds);
      const chunk = <T,>(arr: T[], size: number) => {
        const out: T[][] = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
      };
      const batches = chunk(idList, 10);
      for (const ids of batches) {
        const snap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', ids)));
        snap.forEach(d => {
          const data = d.data() as any;
          userNames.set(d.id, data.name || 'Không xác định');
        });
      }

      // Combine review data with user names
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        reviewsData.push({
          id: doc.id,
          ...data,
          customerName: userNames.get(data.customerId) || 'Không xác định',
          providerName: userNames.get(data.providerId) || 'Không xác định'
        } as Review);
      });

      setReviews(reviewsData);
      setFilteredReviews(reviewsData);
    } catch (err: any) {
      console.error('Error fetching reviews:', err);
      setError('Không thể tải danh sách đánh giá. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // Refetch when server-side filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingFilter, statusFilter]);

  // Memoized client-side filters (search)
  const memoFilteredReviews = useMemo(() => {
    let filtered = [...reviews];
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (review) =>
          review.customerName.toLowerCase().includes(searchLower) ||
          review.comment?.toLowerCase().includes(searchLower)
      );
    }
    return filtered;
  }, [reviews, searchTerm]);

  // Pagination derived state
  const totalPages = useMemo(() => Math.ceil(memoFilteredReviews.length / recordsPerPage) || 1, [memoFilteredReviews.length]);
  const displayedReviews = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return memoFilteredReviews.slice(start, start + recordsPerPage);
  }, [memoFilteredReviews, currentPage]);

  useEffect(() => {
    setFilteredReviews(memoFilteredReviews);
    if (currentPage > totalPages) setCurrentPage(1);
  }, [memoFilteredReviews, currentPage, totalPages]);

  // Render star rating
  const renderStars = (rating: number) => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star ${star <= rating ? 'filled' : 'empty'}`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const startEditing = (review: Review) => {
    setEditingReviewId(review.id);
    setEditingValue(review.status);
  };

  const cancelEditing = () => {
    setEditingReviewId(null);
    setEditingValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent, reviewId: string) => {
    if (e.key === 'Enter') {
      saveReview(reviewId);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const saveReview = async (reviewId: string) => {
    if (updatingStatus) return;

    try {
      setUpdatingStatus(true);
      const reviewRef = doc(db, 'reviews', reviewId);
      await updateDoc(reviewRef, {
        status: editingValue
      });

      // Update local state
      setReviews(reviews.map(review => 
        review.id === reviewId 
          ? { ...review, status: editingValue as 'active' | 'inactive' }
          : review
      ));
      setFilteredReviews(filteredReviews.map(review => 
        review.id === reviewId 
          ? { ...review, status: editingValue as 'active' | 'inactive' }
          : review
      ));

      cancelEditing();
    } catch (error) {
      console.error('Lỗi khi cập nhật trạng thái:', error);
      setError('Không thể cập nhật trạng thái. Vui lòng thử lại.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="manage-reviews">
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-reviews">
      {/* Page Header */}
      <div className="page-header">
        <h2>Quản lý Đánh giá</h2>
        <p className="page-subtitle">
          Xem và quản lý các đánh giá từ khách hàng
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
          <button className="retry-button" onClick={fetchReviews}>
            Thử lại
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>Tìm kiếm</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Tìm theo tên khách hàng hoặc nội dung..."
          />
        </div>

        <div className="filter-group">
          <label>Đánh giá</label>
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
          >
            <option value="all">Tất cả</option>
            <option value="5">5 sao</option>
            <option value="4">4 sao</option>
            <option value="3">3 sao</option>
            <option value="2">2 sao</option>
            <option value="1">1 sao</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Trạng thái</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tất cả</option>
            <option value="active">Hoạt động</option>
            <option value="inactive">Không hoạt động</option>
          </select>
        </div>

        <button className="refresh-button" onClick={fetchReviews}>
          Làm mới
        </button>
      </div>

      {/* Reviews Table */}
      <div className="table-container">
        {filteredReviews.length === 0 ? (
          <div className="no-data-container">
            <div className="no-data">
              {reviews.length === 0
                ? 'Không có đánh giá nào'
                : 'Không tìm thấy đánh giá phù hợp với bộ lọc'}
            </div>
          </div>
        ) : (
          <table className="reviews-table">
            <thead>
              <tr>
                <th>Khách hàng</th>
                <th>Nhà cung cấp</th>
                <th>Đánh giá</th>
                <th>Nội dung</th>
                <th>Thời gian</th>
                <th>Mã booking</th>
                <th>Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {displayedReviews.map((review) => (
                <tr key={review.id} className={review.rating === 1 ? 'row-danger' : ''}>
                  <td className="customer-cell">
                    <div className="customer-name">{review.customerName}</div>
                  </td>
                  <td className="provider-cell">
                    <div className="provider-name">{review.providerName}</div>
                  </td>
                  <td className="rating-cell">
                    {renderStars(review.rating)}
                  </td>
                  <td className="comment-cell">
                    {review.comment || <span className="no-comment">Không có nội dung</span>}
                  </td>
                  <td className="date-cell">
                    {formatDate(review.createdAt)}
                  </td>
                  <td className="booking-cell">
                    <div 
                      className="booking-id clickable"
                      onClick={() => navigate(`/bookings/${review.bookingId}`)}
                      title="Xem chi tiết booking"
                    >
                      {review.bookingId}
                    </div>
                  </td>
                                      <td className="status">
                      {editingReviewId === review.id ? (
                        <select
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => saveReview(review.id)}
                          onKeyPress={(e) => handleKeyPress(e, review.id)}
                          className="status-select"
                        >
                          <option value="active">Hoạt động</option>
                          <option value="inactive">Không hoạt động</option>
                        </select>
                      ) : (
                        <div className="editable-container">
                          <span 
                            className={`status-badge ${review.status} editable`}
                            onClick={() => startEditing(review)}
                            title="Click để chỉnh sửa"
                          >
                            {review.status === 'active' ? 'Đang hoạt động' : 'Không hoạt động'}
                          </span>
                          <button 
                            className="edit-icon-btn"
                            onClick={() => startEditing(review)}
                            title="Chỉnh sửa trạng thái"
                          >
                            ✏️
                          </button>
                        </div>
                      )}
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Hiển thị {(currentPage - 1) * recordsPerPage + 1} - {Math.min(currentPage * recordsPerPage, memoFilteredReviews.length)} của {memoFilteredReviews.length} kết quả
          </div>
          <div className="pagination-controls">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="pagination-btn">««</button>
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="pagination-btn">«</button>
            {(() => {
              const buttons = [] as React.ReactElement[];
              const start = Math.max(1, currentPage - 2);
              const end = Math.min(totalPages, currentPage + 2);
              for (let i = start; i <= end; i++) {
                buttons.push(
                  <button key={i} onClick={() => setCurrentPage(i)} disabled={i === currentPage} className={`pagination-btn ${i === currentPage ? 'active' : ''}`}>{i}</button>
                );
              }
              return buttons;
            })()}
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="pagination-btn">»</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="pagination-btn">»»</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageReviews; 